/* KILLIK — colour strips, swatches and phone menu.
   Everything is set up per section, and set up again when the theme editor
   redraws a section (shopify:section:load), so edits preview live. */
(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Phone menu ── */
  function initMenu(root) {
    const menuBtn = root.querySelector('#menuBtn'), navSheet = root.querySelector('#navSheet');
    if (!menuBtn || !navSheet || menuBtn.dataset.ready) return;
    menuBtn.dataset.ready = '1';
    const setMenu = open => {
      navSheet.hidden = !open;
      menuBtn.setAttribute('aria-expanded', open);
      menuBtn.textContent = open ? 'Close' : 'Menu';
      document.body.classList.toggle('locked', open);
      if (open && !reduced) {
        [...navSheet.children].forEach((a, k) => a.animate(
          [{ transform: 'translateY(-100%)', opacity: 0 }, { transform: 'none', opacity: 1 }],
          { duration: 600, delay: k * 60, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards' }));
      }
    };
    menuBtn.addEventListener('click', () => setMenu(navSheet.hidden));
    navSheet.addEventListener('click', e => { if (e.target.closest('a')) setMenu(false); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && navSheet.isConnected && !navSheet.hidden) setMenu(false); });
    matchMedia('(min-width: 821px)').addEventListener('change', e => { if (e.matches && navSheet.isConnected) setMenu(false); });
  }

  /* ── Strips ── */
  function initStrips(sec) {
    if (!sec || sec.dataset.ready) return;
    const row = sec.querySelector('.strips-row');
    const strips = row ? [...row.querySelectorAll('.strip')] : [];
    if (!strips.length) return;
    sec.dataset.ready = '1';
    const N = strips.length;
    const swatches = sec.querySelector('.swatches');
    const sws = swatches ? [...swatches.querySelectorAll('.sw-b')] : [];
    const autoplay = sec.dataset.autoplay !== 'false' && !window.Shopify?.designMode;   // hold still while editing
    const DUR = 5200;
    let active = -1, timer = 0, hovering = false, inView = false, hoverT = 0;

    // the open strip's final width, so its text never reflows or overhangs
    const setRowW = () => sec.style.setProperty('--row-w', row.clientWidth + 'px');
    setRowW();
    if ('ResizeObserver' in window) new ResizeObserver(setRowW).observe(row);

    function activate(i, { auto = false } = {}) {
      if (i === active) return;
      active = i;
      strips.forEach((el, j) => {
        el.classList.toggle('on', j === i);
        const p = el.querySelector('.s-prog');
        p.classList.remove('run');
        if (j === i && auto) { void p.offsetWidth; p.style.setProperty('--dur', DUR + 'ms'); p.classList.add('run'); }
      });
      sws.forEach((b, j) => {
        b.setAttribute('aria-selected', j === i);
        if (j === i && swatches.offsetParent) swatches.scrollTo({ left: b.offsetLeft - swatches.clientWidth / 2 + b.offsetWidth / 2, behavior: reduced ? 'auto' : 'smooth' });
      });
      const s = strips[i];
      document.documentElement.style.setProperty('--hl', s.dataset.c);
    }
    function schedule() {
      clearTimeout(timer);
      if (!autoplay || reduced || hovering || !inView || N < 2 || !sec.isConnected) return;
      const p = strips[active].querySelector('.s-prog');
      if (!p.classList.contains('run')) { void p.offsetWidth; p.style.setProperty('--dur', DUR + 'ms'); p.classList.add('run'); }
      timer = setTimeout(() => { activate((active + 1) % N, { auto: true }); schedule(); }, DUR);
    }
    function stopAuto() { clearTimeout(timer); strips.forEach(el => el.querySelector('.s-prog').classList.remove('run')); }

    strips.forEach((el, i) => {
      el.addEventListener('pointerenter', e => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(hoverT); hoverT = setTimeout(() => activate(i), 90);
      });
      el.addEventListener('focusin', () => { stopAuto(); activate(i); });
      // first tap on a closed strip opens it; a tap anywhere on an open strip goes to its product page
      el.addEventListener('click', e => {
        if (e.target.closest('.s-buy')) return;              // Buy now does its own thing
        if (active !== i) { e.preventDefault(); stopAuto(); activate(i); return; }
        const link = el.querySelector('.s-hit');
        if (!e.target.closest('.s-hit') && link) { e.preventDefault(); window.location.href = link.href; }
      });
      el.querySelector('.s-hit').addEventListener('keydown', e => {
        const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        const next = (i + dir + N) % N;
        stopAuto(); activate(next);
        strips[next].querySelector('.s-hit').focus();
      });
    });
    swatches && swatches.addEventListener('click', e => {
      const b = e.target.closest('.sw-b'); if (b) { stopAuto(); activate(+b.dataset.i); }
    });

    sec.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') { hovering = true; stopAuto(); } });
    sec.addEventListener('pointerleave', () => { hovering = false; clearTimeout(hoverT); schedule(); });

    // swipe between spirits on phones
    let sx = null, sy = 0, swiped = false;
    row.addEventListener('pointerdown', e => { if (e.pointerType !== 'mouse') { sx = e.clientX; sy = e.clientY; swiped = false; } });
    row.addEventListener('pointerup', e => {
      if (sx === null) return;
      const dx = e.clientX - sx, dy = e.clientY - sy; sx = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { swiped = true; stopAuto(); activate((active + (dx < 0 ? 1 : -1) + N) % N); }
    });
    row.addEventListener('click', e => { if (swiped) { e.preventDefault(); e.stopPropagation(); swiped = false; } }, true);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => { inView = en.isIntersecting; inView ? schedule() : stopAuto(); }, { threshold: .5 }).observe(sec);
    }
    activate(0, { auto: autoplay && !reduced });
  }

  /* ── Product page: Back, and a click on empty space, return to where you came from ── */
  function initBack(root) {
    const page = root.querySelector('.sheet.is-page');
    if (!page || page.dataset.backReady) return;
    page.dataset.backReady = '1';
    const back = page.querySelector('[data-sheet-back]');
    const goBack = () => {
      const fromHere = document.referrer && new URL(document.referrer).origin === location.origin;
      if (fromHere && history.length > 1) history.back(); else location.href = back ? back.href : '/';
    };
    back?.addEventListener('click', e => { e.preventDefault(); goBack(); });
    page.addEventListener('click', e => {
      if (window.Shopify?.designMode) return;
      if (e.target.closest('a, button, input, select, textarea, label, form, summary, details, .shopify-payment-button')) return;
      if (String(window.getSelection?.() || '').trim()) return;   // they were selecting text
      goBack();
    });
  }

  function initAll(root = document) {
    initMenu(root);
    initBack(root);
    root.querySelectorAll('.strips').forEach(initStrips);
  }
  initAll();
  document.addEventListener('shopify:section:load', e => initAll(e.target));
})();
